import type {
  GetPermissionGroupsParams,
  GetPermissionGroupsResponse,
} from '@kizen/api/permission-group';
import type { SortColumn } from 'utility/SortingHelpers';
import type { SettingsState } from '../../SettingsContext';
import type { TeamPageProps } from './types';

import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebounce } from 'react-use';
import Button from '__components/Button';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import Loader from 'components/Kizen/Loader';
import ConfirmDeletionModal from '__components/Modals/presets/ConfirmDeletion';
import { TRow } from '__components/Kizen/Table';
import BasicTable from '__components/Tables/Basic';
import { BasicTableWrapper } from '__components/Tables/Basic';
import TablePagination, {
  TablePaginationWrapper,
} from 'components/Tables/Big/TablePagination';
import TableScrollContainer from '__components/Tables/ScrollContainer';
import { toastVariant, useToast } from '__components/ToastProvider';
import { PermissionGroupHistoryModal } from '__pages/Settings/pages/Team/HistoryModal';
import { PermissionGroupModal } from 'pages/Settings/pages/Team/PermissionGroupModal';
import { KizenTypography } from '__app/typography';
import useSearchParam, { setSearchParams } from 'hooks/useSearchParam';
import { permissionGroupsQuery } from 'query/permission-group';
import { searchTeamMembersQuery } from 'query/team-member';
import { camelToSnakeCase, snakeToCamelCaseKeys } from '__services/helpers';
import TeamMemberService from 'services/TeamMemberService';
import { serviceRefreshAccessStart } from 'store/authentication/authenticationAction';
import { getChosenBusiness } from 'store/authentication/selectors';
import { DEFAULT_INPUT_DELAY } from 'utility/config';
import { getOrderingParam, getSortValue } from 'utility/SortingHelpers';
import { useSettingsContext } from '../../SettingsContext';
import { getPermissionGroupColumns } from './columns';
import { Toolbar } from './Toolbar';
import { openMenuAboveMeta } from './util';

const TEAM_MEMBERS_QUERY_KEY = searchTeamMembersQuery().queryKey;

type Action = 'delete' | 'duplicate' | 'edit' | 'history';

interface PermissionGroupSummary
  extends SnakeToCamelCase<
    Omit<GetPermissionGroupsResponse[number], 'summary'>
  > {
  meta: { openMenuAbove: boolean };
  summary: {
    none: number;
    view: number;
    edit: number;
    remove: number;
  };
}

type EditingPermissionGroup = {
  id?: string;
  name: string;
  [k: string]: any;
};

export const PermissionGroupsPage = ({ access }: TeamPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const [showToast] = useToast();
  const pg = useSearchParam('pg');

  const ctx = useSettingsContext();
  const { search, sort, setSearch, setSort } = ctx.permissionGroups;

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [historyId, setHistoryId] = useState<string | null>(null);
  const [permissionGroup, setPermissionGroup] =
    useState<EditingPermissionGroup | null>(null);

  const chosenBusiness = useSelector(getChosenBusiness);
  const { admin_permission_group: adminPermissionGroup } = chosenBusiness;

  useDebounce(() => setDebouncedSearch(search), DEFAULT_INPUT_DELAY, [search]);

  const queryOptions = useMemo(() => {
    const params: GetPermissionGroupsParams<'paginated'> = {
      include_summary: true,
      ordering: sort,
      page,
      page_size: pageSize,
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    return permissionGroupsQuery({
      params,
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      select: (data) => {
        const { results, ...rest } = data;
        const mapped = results.map((pg, index, arr) => {
          return {
            ...pg,
            meta: openMenuAboveMeta({ index, total: arr.length }),
            summary: {
              none: pg.summary.nb_none,
              view: pg.summary.nb_view,
              edit: pg.summary.nb_edit,
              remove: pg.summary.nb_remove,
            },
          };
        });

        if (sort === 'summary' || sort === '-summary') {
          const iteratees = [
            'summary.remove',
            'summary.edit',
            'summary.view',
            'summary.none',
          ];
          const orders = Array(4).fill(sort === 'summary' ? 'asc' : 'desc');
          const ordered = orderBy(mapped, iteratees, orders);
          return { ...rest, results: snakeToCamelCaseKeys(ordered) };
        }

        return { ...rest, results: snakeToCamelCaseKeys(mapped) };
      },
    });
  }, [page, pageSize, sort, debouncedSearch]);

  const { data, isLoading, isFetching } = useQuery(queryOptions);
  const { results: permissionGroups = [], count } = data ?? {};

  useEffect(() => {
    if (pg && !isLoading) {
      const group = permissionGroups.find((group) => group.id === pg);

      if (group) {
        setPermissionGroup(group);
      }

      setSearchParams(history, { pg: null });
    }
  }, [history, pg, isLoading, permissionGroups]);

  const handleChangeSearch = useCallback(
    (value: string) => {
      setSearch(value);
      setPage(1);
    },
    [setSearch]
  );

  const permissionGroupHeadData = useMemo(
    () => ({
      meta: {
        sort: sort ? getSortValue(sort) : null,
        onSort: (
          column: SnakeToCamelCase<
            SortColumn<SettingsState['permissionGroups']['sort']>
          >,
          direction: 'asc' | 'desc'
        ) => {
          const snake = camelToSnakeCase(column);
          const sort = getOrderingParam({ column: snake, direction });
          setSort(sort);
        },
      },
    }),
    [sort, setSort]
  );

  const permissionGroupColumns = useMemo(() => {
    return getPermissionGroupColumns(
      {
        onClickName: (pg: PermissionGroupSummary) => {
          setPermissionGroup({ id: pg.id, name: pg.name });
        },
        onSelectAction: async (
          action: { value: Action },
          pg: PermissionGroupSummary
        ) => {
          switch (action.value) {
            case 'delete':
              setDeleteId(pg.id);
              break;
            case 'duplicate':
              try {
                await TeamMemberService.duplicatePermissionGroup(pg.id);
              } finally {
                qc.invalidateQueries({
                  queryKey: permissionGroupsQuery().queryKey,
                });
              }
              break;
            case 'edit':
              setPermissionGroup(pg);
              break;
            case 'history':
              setHistoryId(pg.id);
              break;
            default:
              break;
          }
        },
      },
      t,
      access,
      adminPermissionGroup
    );
  }, [t, qc, access, adminPermissionGroup]);

  if (isLoading) {
    return <Loader loading />;
  }

  return (
    <PageSizing>
      {deleteId && (
        <ConfirmDeletionModal
          show
          typeOfContent="text"
          defaultLeftBtnText={t('Cancel')}
          onHide={() => setDeleteId(null)}
          onConfirm={async () => {
            try {
              await TeamMemberService.deletePermissionGroup(deleteId);
              qc.invalidateQueries({ queryKey: TEAM_MEMBERS_QUERY_KEY });
            } catch (err: any) {
              const { data } = err.response;

              if (data.statusCode === 400) {
                showToast({
                  variant: toastVariant.FAILURE,
                  delay: 3000,
                  message: data.errors[0],
                });
              }
            } finally {
              qc.invalidateQueries({
                queryKey: permissionGroupsQuery().queryKey,
              });
              dispatch(serviceRefreshAccessStart() as any);
              setDeleteId(null);
            }
          }}
        >
          <KizenTypography>
            {t('This will permanently delete the Permission Group')}
          </KizenTypography>
        </ConfirmDeletionModal>
      )}
      {permissionGroup && (
        <PermissionGroupModal
          id={permissionGroup.id}
          onHide={() => setPermissionGroup(null)}
          onConfirm={() => {
            qc.invalidateQueries({
              queryKey: permissionGroupsQuery().queryKey,
            });
            dispatch(serviceRefreshAccessStart() as any);
          }}
        />
      )}
      {historyId && (
        <PermissionGroupHistoryModal
          id={historyId}
          onHide={() => setHistoryId(null)}
        />
      )}
      <ContentWidth>
        <Toolbar.Container>
          <Toolbar.LeftColumn>
            <Toolbar.SearchInput
              loading={isFetching}
              placeholder={t('Find Permission Groups')}
              value={search}
              onChange={handleChangeSearch}
            />
          </Toolbar.LeftColumn>
          <Toolbar.Title as="h2" type="header">
            {permissionGroups.length.toLocaleString()}{' '}
            {permissionGroups.length === 1
              ? t('Permission Group')
              : t('Permission Groups')}
          </Toolbar.Title>
          <Toolbar.RightColumn>
            <Button
              style={{ margin: 0 }}
              data-qa="add-permission-group-button"
              onClick={() => setPermissionGroup({ name: '' })}
            >
              {t('New Permission Group')}
            </Button>
          </Toolbar.RightColumn>
        </Toolbar.Container>
        <BasicTableWrapper className="shadow-tile">
          <TableScrollContainer bottom>
            <BasicTable
              stickyHeader
              data-qa="permission-group-table"
              head={
                <TRow
                  head
                  columns={permissionGroupColumns}
                  data={permissionGroupHeadData}
                />
              }
            >
              {permissionGroups.map((permissionGroup) => (
                <TRow
                  key={permissionGroup.id}
                  columns={permissionGroupColumns}
                  data={permissionGroup}
                />
              ))}
            </BasicTable>
          </TableScrollContainer>
        </BasicTableWrapper>
        <TablePaginationWrapper>
          <TablePagination
            page={page}
            perPage={pageSize}
            totalCount={count}
            onChangePage={(value: number) => {
              setPage(value);
            }}
            onChangePerPage={(value: number) => {
              setPageSize(value);
            }}
          />
        </TablePaginationWrapper>
      </ContentWidth>
    </PageSizing>
  );
};
